import { Injectable } from '@angular/core';
import { INDRABookedTripDetails, INDRAPnrDetailsServiceResponse } from '@ibe.models/booked-trip-details.indra.model.lib';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { INDRAInfoPax, INDRAPassengerTierBenefitInfo } from '@ibe.models/pnr-data.indra.model.lib';
import { CountryCode } from '@ibe.models/country-code.model';
import { INDRAATCAvailabilityResponse, INDRAATCAvailabilityRequest } from '@ibe.models/atc.availability.indra.model.lib';
import { INDRAPax } from '@ibe.models/pax.indra.model.lib';
import { INDRAStorageService } from './storage.indra.service';
import { INDRAPNRSearchParameters } from '@ibe.models/booked-trips.indra.model.lib';
import { INDRATotalSummaryDetails } from '@ibe.models/total_summary.model';
import { INDRAAddOnAncillary } from '@ibe.models/ancillary.indra.model.lib';
import { INDRABookingStep } from '@ibe.components/booking/steps/booking-steps/bookingStep.indra';
import { INDRAPaymentMethod } from '@ibe.models/payment_method.model';
import * as _ from 'lodash';


@Injectable({ providedIn: 'root' })
export class INDRABFMDataStorageService {
	
	private _activeBooking: INDRABookedTripDetails; //searched Booking to be used by sidebar components
	private _originalPNRDetailsResponse: INDRAPnrDetailsServiceResponse; // copy of the original response retrieved from pnr search service..
	private _countryCode: CountryCode[];
	private _selectedCancel: INDRAPax[];
	private readonly _observableCurrentBooking: BehaviorSubject<INDRABookedTripDetails>;

	private _atcAvailability: INDRAATCAvailabilityResponse;
	private _lastName: string;
	private _atcRequest: INDRAATCAvailabilityRequest;
	private _cancelledDetails: INDRAPNRSearchParameters;
	private _selectedFlightBound: string;
	private totalSummeryDetails: INDRATotalSummaryDetails;
	private _selectedFlightChange = undefined;
	private _flightToBeChange = undefined;
	private _selectedFlightUpgradeCash = undefined;
	private _selectedBrandUpgradeCash = undefined;
	private _selectedBrandToChange = undefined;
	private _deletedPreviouslyBoughtSeats: INDRAAddOnAncillary[];

	private _flight_tickets;
	private _paxToBeChange = undefined;

	private selectedBrandSubject = new Subject<any>();
	private _bagCounts: any;

	private _selectFlightBookingSteps: Array<INDRABookingStep>;
	private _selectFlightCurrentStep: number;

	private _selectedPassengersForUpgrade: number[];
	private _upgradeBrands: any;
	private _selectedUpgradeBrand: any;
	private _brandList: any;
	private _paymentMethod: INDRAPaymentMethod;
	
	/**** phase 2 additions *****/
	private _bookingAvailability: any;
	private _thirdPartyAncillaries: any;
	private _ancillariesPacks: any;
	private _timeToThink: any;
	private _ancillariesPerFlight: any;
	private _dupeBooking: any;
	private _travelDocumentationConfirm: boolean;
	private _travelHazmatBaggageRulesConfirm: boolean;
	private _availabilityDatelist$: BehaviorSubject<any>;

	private _timeToThinkCode: string;
	timeToThinkCode: BehaviorSubject<string>;
	private _timeToThinkOptions: any;

	constructor(private storageService: INDRAStorageService) {
		this._observableCurrentBooking = new BehaviorSubject(this.activeBooking);
		this._availabilityDatelist$ = new BehaviorSubject(null);

		this.timeToThinkCode = new BehaviorSubject(this._timeToThinkCode);
	}
	
	/**
	 * Retrieves a booked trip from the data store.
	 */
	INDRA_getActiveBookedTrip(): INDRABookedTripDetails {
		if (this._activeBooking == undefined)
			this._activeBooking = this.storageService.get("INDRABFMDataStorageService._activeBooking");
		return this._activeBooking;
	}

	/**
	 * Stores the booked trip data.
	 * @param {string} booking - the booked trip to be stored
	 */
	INDRA_setActiveBookedTrip(booking: INDRABookedTripDetails) {
		this._activeBooking = booking;
		this.storageService.set("INDRABFMDataStorageService._activeBooking", this._activeBooking);
		this._observableCurrentBooking.next(this._activeBooking);
	}

	INDRA_getObservableCurrentBooking(): Observable<INDRABookedTripDetails> {
		return this._observableCurrentBooking.asObservable();
	}

	INDRA_getActiveBookInfoPax(): INDRAInfoPax {
		return this.INDRA_getActiveBookedTrip() && this.INDRA_getActiveBookedTrip().data ? this.INDRA_getActiveBookedTrip().data.infoPax : null;
	}

	INDRA_setActiveBookInfoPax(infoPax: INDRAInfoPax) {
		this._activeBooking = this.INDRA_getActiveBookedTrip()
		const activeBooking = {
			...this._activeBooking,
			data: {
				...this._activeBooking.data,
				infoPax: infoPax
			}
		};
		this.storageService.set("INDRABFMDataStorageService._activeBooking", activeBooking);
		this._activeBooking = this.storageService.get("INDRABFMDataStorageService._activeBooking");
	}

	INDRA_setTierInformation(tierInformation) {
		this._activeBooking = this.INDRA_getActiveBookedTrip();

		const newTierInformation = {...this._activeBooking['tier-information']};
		Object.keys(tierInformation).forEach(tier => {
			if (!newTierInformation[tier]) newTierInformation[tier] = tierInformation[tier];
		});

		const activeBooking = {
			...this._activeBooking,
			'tier-information': newTierInformation
		};
		this.storageService.set("INDRABFMDataStorageService._activeBooking", activeBooking);
		this._activeBooking = this.storageService.get("INDRABFMDataStorageService._activeBooking");
	}

	INDRA_setPassengerTierBenefitInfo(newPassengerTierBenefitInfo: INDRAPassengerTierBenefitInfo[]) {
		this._activeBooking = this.INDRA_getActiveBookedTrip();

		const tierBenefitInfo = this._activeBooking.data.passengerTierBenefitInfo ? _.cloneDeep(this._activeBooking.data.passengerTierBenefitInfo) : [];
		newPassengerTierBenefitInfo.forEach(pax => {
			const i = tierBenefitInfo.findIndex(oldPax => oldPax.paxId === pax.paxId);
			i === -1 ? tierBenefitInfo.push(pax) : tierBenefitInfo[i] = pax;
		});

		const activeBooking = {
			...this._activeBooking,
			data: {
				...this._activeBooking.data,
				passengerTierBenefitInfo: tierBenefitInfo
			}
		};

		this.storageService.set("INDRABFMDataStorageService._activeBooking", activeBooking);
		this._activeBooking = this.storageService.get("INDRABFMDataStorageService._activeBooking");
	}

	INDRA_getCountryCodes() {
		if (this._countryCode == undefined) {
			this._countryCode = this.storageService.get("INDRABFMDataStorageService._countryCode");
		}
		return this._countryCode;
	}

	INDRA_setCountryCodes(countryCode: CountryCode[]) {
		this._countryCode = countryCode;
		this.storageService.set("INDRABFMDataStorageService._countryCode", this._countryCode);
	}

	get activeBooking() {
		return this.INDRA_getActiveBookedTrip();
	}

	set activeBooking(booking: INDRABookedTripDetails) {
		this._activeBooking = booking;
		this.storageService.set("INDRABFMDataStorageService._activeBooking", this._activeBooking);
	}

	get observableCurrentBooking() {
		return this._observableCurrentBooking;
	}

	INDRA_setAtcAvailability(param: INDRAATCAvailabilityResponse) {
		this._atcAvailability = param;
		this.storageService.set("INDRABFMDataStorageService._atcAvailability", this._atcAvailability);
	}

	INDRA_getAtcAvailability(): INDRAATCAvailabilityResponse {
		if (this._atcAvailability === undefined) {
			this._atcAvailability = this.storageService.get("INDRABFMDataStorageService._atcAvailability");
		}
		return this._atcAvailability;
	}

	INDRA_getSelectedForCancel(): INDRAPax[] {
		if (this._selectedCancel === undefined) {
			this._selectedCancel = this.storageService.get("INDRABFMDataStorageService._selectedCancel");
		}
		return this._selectedCancel ? this._selectedCancel : [];
	}

	INDRA_setSelectedForCancel(selectedPassengers: INDRAPax[]): void {
		this._selectedCancel = selectedPassengers;
		this.storageService.set("INDRABFMDataStorageService._selectedCancel", this._selectedCancel);
	}

	INDRA_setLastName(lastName) {
		this._lastName = lastName;
	}

	INDRA_getLastName() {
		if (this._lastName == undefined) {
			this._lastName = this.storageService.get("lastname");
		}
		return this._lastName ? this._lastName : '';
	}

	INDRA_getAtcRequest(): INDRAATCAvailabilityRequest {
		if (this._atcRequest == undefined) {
			this._atcRequest = this.storageService.get("INDRABFMDataStorageService._atcRequest");
		}
		return this._atcRequest;
	}


	INDRA_setAtcRequest(atcRequest: INDRAATCAvailabilityRequest) {
		this._atcRequest = atcRequest;
		this.storageService.set("INDRABFMDataStorageService._atcRequest", this._atcRequest);
	}

	INDRA_getCancelledDetails(): INDRAPNRSearchParameters {
		if (this._cancelledDetails == undefined) {
			this._cancelledDetails = this.storageService.get("INDRABFMDataStorageService._cancelledDetails");
		}
		return this._cancelledDetails;
	}

	INDRA_setCancelledDetails(pnrDetails: INDRAPNRSearchParameters) {
		this._cancelledDetails = pnrDetails;
		this.storageService.set("INDRABFMDataStorageService._cancelledDetails", this._cancelledDetails);
	}

	INDRA_getBoundSelectedFlight() {
		if (this._selectedFlightBound == undefined) {
			this._selectedFlightBound = this.storageService.get("INDRABFMDataStorageService._selectedFlightBound");
		}
		return this._selectedFlightBound ? this._selectedFlightBound : '';
	}

	INDRA_setBoundSelectedFlight(bound: string) {
		this._selectedFlightBound = bound;
		this.storageService.set("INDRABFMDataStorageService._selectedFlightBound", this._selectedFlightBound);
	}

	INDRA_setTotalSummaryDetails(details: INDRATotalSummaryDetails) {
		this.totalSummeryDetails = details;
		this.storageService.set("INDRABFMDataStorageService.totalSummeryDetails", this.totalSummeryDetails);
	}

	INDRA_getTotalSummaryDetails(): INDRATotalSummaryDetails {
		if (this.totalSummeryDetails == undefined) {
			this.totalSummeryDetails = this.storageService.get("INDRABFMDataStorageService.totalSummeryDetails");
		}
		return this.totalSummeryDetails;
	}

	INDRA_setSelectedFlightChange(flight: any) {
		this._selectedFlightChange = flight;
		this.storageService.set("INDRABFMDataStorageService._selectedFlightChange", this._selectedFlightChange);
	}

	INDRA_getSelectedFlightChange() {
		if (this._selectedFlightChange == undefined) {
			this._selectedFlightChange = this.storageService.get("INDRABFMDataStorageService._selectedFlightChange");
		}
		return this._selectedFlightChange ? this._selectedFlightChange : null;
	}

	INDRA_setFlightToBeChange(flight: any) {
		this._flightToBeChange = flight;
		this.storageService.set("INDRABFMDataStorageService._flightToBeChange", this._flightToBeChange);
	}

	INDRA_getFlightToBeChange() {
		if (this._flightToBeChange == undefined) {
			this._flightToBeChange = this.storageService.get("INDRABFMDataStorageService._flightToBeChange");
		}
		return this._flightToBeChange ? this._flightToBeChange : null;
	}

	INDRA_resetFlightChange() {
		this.INDRA_setFlightToBeChange(null);
		this.INDRA_setSelectedFlightChange(null);
	}

	INDRA_getFlightToUpgradeCash() {
		if (this._selectedFlightUpgradeCash == undefined) {
			this._selectedFlightUpgradeCash = this.storageService.get("INDRABFMDataStorageService._selectedFlightUpgradeCash");
		}
		return this._selectedFlightUpgradeCash ? this._selectedFlightUpgradeCash : null;
	}

	INDRA_setFlightToUpgradeCash(selectedFlight) {
		this._selectedFlightUpgradeCash = selectedFlight;
		this.storageService.set("INDRABFMDataStorageService._selectedFlightUpgradeCash", this._selectedFlightUpgradeCash);
	}

	INDRA_getSelectedBrandToUpgradeCash() {
		if (this._selectedBrandUpgradeCash == undefined) {
			this._selectedBrandUpgradeCash = this.storageService.get("INDRABFMDataStorageService._selectedBrandUpgradeCash");
		}
		return this._selectedBrandUpgradeCash ? this._selectedBrandUpgradeCash : null;
	}

	INDRA_setSelectedBrandToUpgradeCash(brand: any) {
		this._selectedBrandUpgradeCash = brand;
		this.storageService.set("INDRABFMDataStorageService._selectedBrandUpgradeCash", this._selectedBrandUpgradeCash);
	}

	INDRA_getSelectedBrandToChange() {
		if (this._selectedBrandToChange == undefined) {
			this._selectedBrandToChange = this.storageService.get("INDRABFMDataStorageService._selectedBrandToChange");
		}
		return this._selectedBrandToChange ? this._selectedBrandToChange : null;
	}

	INDRA_setSelectedBrandToChange(brand: any) {
		this._selectedBrandToChange = brand;
		this.storageService.set("INDRABFMDataStorageService._selectedBrandToChange", this._selectedBrandToChange);
	}

	INDRA_getDeletedPreviouslyPurchasedSeats() {
		if (this._deletedPreviouslyBoughtSeats == undefined) {
			this._deletedPreviouslyBoughtSeats = this.storageService.get("INDRABFMDataStorageService._deletedPreviouslyBoughtSeats");
		}
		return this._deletedPreviouslyBoughtSeats ? this._deletedPreviouslyBoughtSeats  : [];
	}

	INDRA_setDeletedPreviouslyPurchasedSeats(seats: INDRAAddOnAncillary[]) {
		this._deletedPreviouslyBoughtSeats = seats;
		this.storageService.set("INDRABFMDataStorageService._deletedPreviouslyBoughtSeats", this._deletedPreviouslyBoughtSeats);
	}

	INDRA_setFlightTickets(flight_tickets: any) {
		this._flight_tickets = flight_tickets;
		this.storageService.set("INDRABFMDataStorageService._flight_tickets", this._flight_tickets);
	}

	INDRA_getFlightTickets() {
		if (this._flight_tickets == undefined) {
			this._flight_tickets = this.storageService.get("INDRABFMDataStorageService._flight_tickets");
		}
		return this._flight_tickets ? this._flight_tickets : [];
	}

	INDRA_setPassengersToBeChange(pax: any ) {
		this._paxToBeChange = pax;
		this.storageService.set("INDRABFMDataStorageService._paxToBeChange", this._paxToBeChange);
	}

	INDRA_getPassengersToBeChange() {
		if (this._paxToBeChange == undefined) {
			this._paxToBeChange = this.storageService.get("INDRABFMDataStorageService._paxToBeChange");
		}
		return this._paxToBeChange ? this._paxToBeChange : null;
	}

	INDRA_setSelectedBrand(brand) {
		this.selectedBrandSubject.next(brand);
	}

	INDRA_getSelectedBrand(): Observable<any> {
		return this.selectedBrandSubject.asObservable();
	}

	INDRA_getBagCounts() {
		if (this._bagCounts == undefined) {
			this._bagCounts = this.storageService.get("INDRABFMDataStorageService._bagCounts");
		}
		return this._bagCounts ? this._bagCounts : [];
	}

	INDRA_pushBagCountItem(bagCounts) {
		this._bagCounts.push(bagCounts);
		this.storageService.set("INDRABFMDataStorageService._bagCounts", this._bagCounts);
	}


	INDRA_setOriginalPNRDetailsResponse(response: INDRAPnrDetailsServiceResponse) {
		this._originalPNRDetailsResponse = response;
		this.storageService.set("INDRABFMDataStorageService._originalPNRDetailsResponse", this._originalPNRDetailsResponse);
	}

	get originalPNRDetailsResponse() {
		if (this._originalPNRDetailsResponse == undefined) {
			this._originalPNRDetailsResponse = this.storageService.get("INDRABFMDataStorageService._originalPNRDetailsResponse");
		}
		return this._originalPNRDetailsResponse;
	}

	INDRA_setSelectedBrandIndexToChange(brand: any, index) {
		if (!this._selectedBrandToChange || this._selectedBrandToChange.length <= 0) {
			this._selectedBrandToChange = [];
		}
		this._selectedBrandToChange[index] = brand[index];
		this.storageService.set("INDRABFMDataStorageService._selectedBrandToChange", this._selectedBrandToChange);
	}

	INDRA_setSelectedFlightIndexChange(flight: any, index) {

		if (!this._selectedFlightChange || this._selectedFlightChange.length <= 0) {
			this._selectedFlightChange = [];
		}
		this._selectedFlightChange[index] = flight[index];
		this.storageService.set("INDRABFMDataStorageService._selectedFlightChange", this._selectedFlightChange);
	}

	INDRA_setSelectFlightBookingSteps(steps: Array<INDRABookingStep>) {
		this._selectFlightBookingSteps = steps;
		this.storageService.set("INDRABFMDataStorageService._selectFlightBookingSteps", this._selectFlightBookingSteps);
	}

	INDRA_getSelectFlightBookingSteps():  Array<INDRABookingStep> {
		if (this._selectFlightBookingSteps == undefined) {
			let steps = [];
			this._selectFlightBookingSteps = [];
			steps = this.storageService.get("INDRABFMDataStorageService._selectFlightBookingSteps");
			steps.forEach(step => {
				this._selectFlightBookingSteps.push(new INDRABookingStep(step._departure, step._destination, step._date, step._bound));
			});
		}
		return this._selectFlightBookingSteps;
	}

	INDRA_setSelectFlightCurrentStep(step: number) {
		this._selectFlightCurrentStep = step;
		this.storageService.set("INDRABFMDataStorageService._selectFlightCurrentStep", this._selectFlightCurrentStep);
	}

	INDRA_getSelectFlightCurrentStep() {
		if (this._selectFlightCurrentStep == undefined) {
			this._selectFlightCurrentStep = this.storageService.get("INDRABFMDataStorageService._selectFlightCurrentStep");
		}
		return this._selectFlightCurrentStep ? this._selectFlightCurrentStep : 0;
	}

	INDRA_getSelectedPassengersForUpgrade(): any [] {
		if (this._selectedPassengersForUpgrade == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._selectedPassengerForUpgrade');
			if (sessionValue != 'undefined') {
				this._selectedPassengersForUpgrade = sessionValue;
			}
		}
		return this._selectedPassengersForUpgrade ? this._selectedPassengersForUpgrade : [];
	}

	INDRA_setSelectedPassengersForUpgrade(selectedIndex: any[]) {
		this._selectedPassengersForUpgrade = selectedIndex;
		if (selectedIndex) {
			this.storageService.set('INDRABFMDataStorageService._selectedPassengerForUpgrade', this._selectedPassengersForUpgrade);
		} else {
			this.storageService.remove('INDRABFMDataStorageService._selectedPassengerForUpgrade');
		}
	}

	INDRA_getUpgradeBrands(): any [] {
		if (this._upgradeBrands == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._upgradeBrands');
			if (sessionValue != 'undefined') {
				this._upgradeBrands = sessionValue;
			}
		}
		return this._upgradeBrands ? this._upgradeBrands : undefined;
	}

	INDRA_setUpgradeBrands(brands: any[]) {
		this._upgradeBrands = brands;
		if (brands) {
			this.storageService.set('INDRABFMDataStorageService._upgradeBrands', this._upgradeBrands);
		} else {
			this.storageService.remove('INDRABFMDataStorageService._upgradeBrands');
		}
	}

	INDRA_getSelectedBrandUpgrade(): any {
		if (this._selectedUpgradeBrand == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._selectedUpgradeBrand');
			if (sessionValue != 'undefined') {
				this._selectedUpgradeBrand = sessionValue;
			}
		}
		return this._selectedUpgradeBrand ? this._selectedUpgradeBrand : undefined;
	}

	INDRA_setSelectedUpgradeBrand(selectedBrand: any) {
		this._selectedUpgradeBrand = selectedBrand;
		if (selectedBrand) {
			this.storageService.set('INDRABFMDataStorageService._selectedUpgradeBrand', this._selectedUpgradeBrand);
		} else {
			this.storageService.remove('INDRABFMDataStorageService._selectedUpgradeBrand');
		}
	}

	INDRA_getBrandList(): any {
		if (this._brandList == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._brandList');
			if (sessionValue != 'undefined') {
				this._brandList = sessionValue;
			}
		}
		return this._brandList ? this._brandList : undefined;
	}

	INDRA_setBrandList(brandList: any) {
		this._brandList = brandList;
		if (brandList) {
			this.storageService.set('INDRABFMDataStorageService._brandList', this._brandList);
		} else {
			this.storageService.remove('INDRABFMDataStorageService._brandList');
		}
	}

	INDRA_setPaymentMethod(paymentMethod: INDRAPaymentMethod) {
		this._paymentMethod = paymentMethod;
	}

	INDRA_getPaymentMethod() {
		return this._paymentMethod;
	}


	/******************* PHASE 2 CODE ADDITION *******************/
	
	INDRA_setBookingAvailability(param: any) {
		this._bookingAvailability = param;
		// this.storageService.set('INDRABFMDataStorageService._bookingAvailability', this._bookingAvailability);
	}

	INDRA_getBookingAvailability(): any {
		/*
		if (this._bookingAvailability === undefined) {
			this._bookingAvailability = this.storageService.get('INDRABFMDataStorageService._bookingAvailability');
		}
		*/
		return this._bookingAvailability;
	}

	INDRA_setStopoverInboundDate(date) {
		this.storageService.set('INDRAStopoverInboundDate', date);
	}

	INDRA_getStopoverInboundDate() {
		return this.storageService.get('INDRAStopoverInboundDate');
	}

	INDRA_removeStopoverInboundDate() {
		this.storageService.remove('INDRAStopoverInboundDate');
	}
	
	INDRA_setStopoverOutboundDate(date) {
		this.storageService.set('INDRAStopoverOutboundDate', date);
	}

	INDRA_getStopoverOutboundDate() {
		return this.storageService.get('INDRAStopoverOutboundDate');
	}

	INDRA_removeStopoverOutboundDate() {
		this.storageService.remove('INDRAStopoverOutboundDate');
	}
	
	INDRA_setOneWayStopoverOutboundDate(date) {
		this.storageService.set('INDRAOneWayStopoverOutboundDate', date);
	}

	INDRA_getOneWayStopoverOutboundDate() {
		return this.storageService.get('INDRAOneWayStopoverOutboundDate');
	}

	INDRA_removeOneWayStopoverOutboundDate() {
		this.storageService.remove('INDRAOneWayStopoverOutboundDate');
	}

	INDRA_setStopoverDestination(location: string) {
		this.storageService.set('INDRAStopoverDestination', location);
	}

	INDRA_getStopoverDestination() {
		return this.storageService.get('INDRAStopoverDestination');
	}

	INDRA_removeStopoverDestination() {
		this.storageService.remove('INDRAStopoverDestination');
	}
	
	
	
	/**
	 * Stores third party ancillaries (e.g. cars, hotels).
	 * @param {string} booking - the booked trip to be stored
	 */
	INDRA_setThirdPartyAncillaries(bookingExtras) {
		this._thirdPartyAncillaries = bookingExtras;
		this.storageService.set("INDRABFMDataStorageService._thirdPartyAncillaries", this._thirdPartyAncillaries);
	}
	
	/**
	 * Returns third party ancillaries (e.g. cars, hotels).
	 */
	INDRA_getThirdPartyAncillaries() {
		if (this._thirdPartyAncillaries == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._thirdPartyAncillaries');
			if (sessionValue != 'undefined') {
				this._thirdPartyAncillaries = sessionValue;
			}
		}
		return this._thirdPartyAncillaries ? this._thirdPartyAncillaries : undefined;
	}

	/**
	 * Stores ancillaries packs.
	 * @param {string} bookingPacks - ancillaries packs
	 */
	INDRA_setAncillariesPacks(bookingPacks) {
		this._ancillariesPacks = bookingPacks;
		this.storageService.set("INDRABFMDataStorageService._ancillariesPacks", this._ancillariesPacks);
	}
	
	/**
	 * Returns ancillaries packs.
	 */
	INDRA_getAncillariesPacks() {
		if (this._ancillariesPacks == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._ancillariesPacks');
			if (sessionValue != 'undefined') {
				this._ancillariesPacks = sessionValue;
			}
		}
		return this._ancillariesPacks ? this._ancillariesPacks : undefined;
	}

	INDRA_setTimeToThink(timeToThink: any) {
		this._timeToThink = timeToThink;
		if (timeToThink) {
			this.storageService.set('INDRABFMDataStorageService._timeToThink', this._timeToThink);
		} else {
			this.storageService.remove('INDRABFMDataStorageService._timeToThink');
		}
	}

	INDRA_getTimeToThink() {
		if (this._timeToThink == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._timeToThink');
			if (sessionValue != 'undefined') {
				this._timeToThink = sessionValue;
			}
		}
		return this._timeToThink ? this._timeToThink : undefined;
	}

	/**
	 * Stores available ancillaries per flight
	 */
	INDRA_setAncillariesPerFlight(ancillaryDetails) {
		this._ancillariesPerFlight = ancillaryDetails;
		this.storageService.set('INDRABFMDataStorageService._ancillariesPerFlight', this._ancillariesPerFlight);
	}

	/**
	 * Returns available ancillaries per flight
	 */
	INDRA_getAncillariesPerFlight() {
		if (this._ancillariesPerFlight == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._ancillariesPerFlight');
			if (sessionValue != 'undefined') {
				this._ancillariesPerFlight = sessionValue;
			}
		}
		return this._ancillariesPerFlight ? this._ancillariesPerFlight : undefined;
	}

	/**
	 * Retrieves a duplicate booked trip from the data store.
	 */
	INDRA_getDupeBookedTrip(): INDRABookedTripDetails {
		if (this._dupeBooking == undefined)
			this._dupeBooking = this.storageService.get('INDRABFMDataStorageService._dupeBooking');
		return this._dupeBooking;
	}

	/**
	 * Stores the duplicate booked trip data.
	 * @param {string} booking - the duplicate booked trip to be stored
	 */
	INDRA_setDupeBookedTrip(booking: INDRABookedTripDetails) {
		this._dupeBooking = booking;
		this.storageService.set('INDRABFMDataStorageService._dupeBooking', this._dupeBooking);
		this._observableCurrentBooking.next(this._dupeBooking);
	}
	
	/**
	 * Retrieves user travel documentation confirmation from the data store.
	 */
	INDRA_getTravelDocumentationConfirm(): boolean {
		if (this._travelDocumentationConfirm == undefined)
			this._travelDocumentationConfirm = this.storageService.get('INDRABFMDataStorageService._travelDocumentationConfirm');
		return this._travelDocumentationConfirm;
	}
	
	/**
	 * Stores user travel documentation confirmation data.
	 * @param {string} booking - the duplicate booked trip to be stored
	 */
	INDRA_setTravelDocumentationConfirm(travelDocumentationConfirm: boolean) {
		this._travelDocumentationConfirm = travelDocumentationConfirm;
		this.storageService.set('INDRABFMDataStorageService._travelDocumentationConfirm', this._travelDocumentationConfirm);
	}
	
	/**
	 * Retrieves user hazmat and baggage rules from the data store.
	 */
	INDRA_getTravelHazmatBaggageRulesConfirm(): boolean {
		if (this._travelHazmatBaggageRulesConfirm == undefined)
			this._travelHazmatBaggageRulesConfirm = this.storageService.get('INDRABFMDataStorageService._travelHazmatBaggageRulesConfirm');
		return this._travelHazmatBaggageRulesConfirm;
	}
	
	/**
	 * Stores user hazmat and baggage rules confirmation data.
	 * @param {string} booking - the duplicate booked trip to be stored
	 */
	INDRA_setTravelHazmatBaggageRulesConfirm(travelHazmatBaggageRulesConfirm: boolean) {
		this._travelHazmatBaggageRulesConfirm = travelHazmatBaggageRulesConfirm;
		this.storageService.set('INDRABFMDataStorageService._travelHazmatBaggageRulesConfirm', this._travelHazmatBaggageRulesConfirm);
	}

	/**
	 * Stores matrix to compare if lowest price changes
	 */
	INDRA_setAvailabilityDatelist(param: any) {
		this._availabilityDatelist$.next(param);
	}

	INDRA_getAvailabilityDatelist(): BehaviorSubject<any> {
		return this._availabilityDatelist$;
	}

	INDRA_setTimeToThinkCode(code: string) {
		this._timeToThinkCode = code ;
		this.timeToThinkCode.next(this._timeToThinkCode);
	}

	INDRA_setTimeToThinkOptions(timeToThinkOptions: any) {
		this._timeToThinkOptions = timeToThinkOptions;
		if (timeToThinkOptions) {
			this.storageService.set('INDRABFMDataStorageService._timeToThinkOptions', this._timeToThinkOptions);
		} else {
			this.storageService.remove('INDRABFMDataStorageService._timeToThinkOptions');
		}
	}

	INDRA_getTimeToThinkOptions() {
		if (this._timeToThinkOptions == undefined) {
			const sessionValue = this.storageService.get('INDRABFMDataStorageService._timeToThinkOptions');
			if (sessionValue != 'undefined') {
				this._timeToThinkOptions = sessionValue;
			}
		}
		return this._timeToThinkOptions ? this._timeToThinkOptions : undefined;
	}

	INDRA_removeTimeToThinkOptions() {
		this.storageService.remove('INDRABFMDataStorageService._timeToThinkOptions');
	}
}
